<script setup>
// Hover cluster placed inside a table cell (sibling of .cell-content, so it
// overlays widget output without living inside the cell's render function).
// Emits filter-for / filter-out immediately; the optional magnifier drills in.
//
// Buttons stop mousedown/click propagation so they never trigger cell-select.

defineProps({
  // Show the drill-in (magnifier) button — only where the table supports zoom.
  showDrill: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['filter-for', 'filter-out', 'drill'])
</script>

<template>
  <div class="cell-filter-buttons" @mousedown.stop @click.stop>
    <button
      class="cfb-btn cfb-for"
      title="Filter for value"
      @click="emit('filter-for')"
    >
      <font-awesome-icon :icon="['fas', 'plus']" />
    </button>
    <button
      class="cfb-btn cfb-out"
      title="Filter out value"
      @click="emit('filter-out')"
    >
      <font-awesome-icon :icon="['fas', 'minus']" />
    </button>
    <button
      v-if="showDrill"
      class="cfb-btn cfb-drill"
      title="Open"
      @click="emit('drill')"
    >
      <font-awesome-icon :icon="['fas', 'magnifying-glass']" />
    </button>
  </div>
</template>

<style scoped>
.cell-filter-buttons {
  position: absolute;
  top: 50%;
  right: 0.375rem;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 0.1875rem;
  z-index: 3;
}

/*
 * Hide/reveal is fully global (not scope-bound) so it reliably responds to the
 * parent cell's :hover regardless of component scope-id boundaries. The parent
 * cell carries .has-cell-filters.
 */
:global(.has-cell-filters .cell-filter-buttons) {
  opacity: 0;
  pointer-events: none;
  transition: opacity var(--transition-fast, 0.15s) ease;
}

:global(.has-cell-filters:hover .cell-filter-buttons) {
  opacity: 0.75;
  pointer-events: auto;
}

.cfb-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.125rem;
  height: 1.125rem;
  padding: 0;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-xs);
  background: var(--surface-card);
  color: var(--text-faint);
  cursor: pointer;
  font-size: 0.55rem;
  transition: all var(--transition-fast, 0.15s) ease;
}

/* Whole cluster comes to full strength when the buttons themselves are hovered */
.cell-filter-buttons:hover {
  opacity: 1 !important;
}

.cfb-for:hover {
  background: var(--accent-soft-3);
  border-color: rgba(115, 87, 210, 0.4);
  color: var(--accent-text);
}

.cfb-out:hover {
  background: var(--color-error-soft, rgba(239, 68, 68, 0.12));
  border-color: rgba(239, 68, 68, 0.4);
  color: var(--color-error);
}

.cfb-drill:hover {
  background: var(--surface-hover);
  color: var(--text-primary);
}
</style>
